// Timeline — хронология построения снизу. Каждая операция → карточка.
//
// Поведение:
//   Клик          — выделить feature (для дальнейших действий)
//   Двойной клик  — открыть параметры в правой Properties-панели (edit-mode)
//   Правый клик   — контекстное меню (Удалить / Подавить / Переименовать)
//   Кнопка ✎      — то же что двойной клик
//
// Features текут слева→направо в порядке создания. При rebuild играем все
// по порядку.

import React, { useState } from 'react';
import RU from '../i18n/ru';
import useCadStore from '../store/useCadStore';
import { rebuildAllBodies } from '../rebuildPipeline';
import {
    IconSketchText,
    IconSketch, IconExtrude, IconRevolve, IconSweep, IconLoft,
    IconFillet, IconChamfer, IconHole, IconMirror, IconPattern, IconCircPattern,
    IconBox, IconCylinder, IconSphere, IconTorus, IconCombine, IconPlane,
} from './icons';
import cl from './Timeline.module.css';


// Мелкие инлайн-иконки для кнопок карточки (без эмодзи).
const IconEdit = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z"/>
    </svg>
);
const IconTrash = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14"/>
    </svg>
);
const IconClock = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/>
    </svg>
);

// SVG-иконки фич (без эмодзи — правило проекта).
const FEATURE_ICONS = {
    sketch:   IconSketch,
    extrude:  IconExtrude,
    revolve:  IconRevolve,
    sweep:    IconSweep,
    loft:     IconLoft,
    fillet:   IconFillet,
    chamfer:  IconChamfer,
    hole:     IconHole,
    text:     IconSketchText,
    mirror:   IconMirror,
    rectPattern: IconPattern,
    circPattern: IconCircPattern,
    box:      IconBox,
    cylinder: IconCylinder,
    sphere:   IconSphere,
    torus:    IconTorus,
    boolean:  IconCombine,
    combine:  IconCombine,
    plane:    IconPlane,
};

// Возвращает SVG-компонент иконки для фичи.
function iconForFeature(feature) {
    if (feature.kind === 'primitive') {
        return FEATURE_ICONS[feature.primitiveKind] || IconBox;
    }
    return FEATURE_ICONS[feature.kind] || IconExtrude;
}


const featureLabel = (feature) => {
    if (feature.label) return feature.label;
    if (feature.kind === 'sketch') {
        const plane = feature.planeRef?.plane || '';
        return `Эскиз${plane ? ` (${plane})` : ''}`;
    }
    if (feature.kind === 'extrude') {
        const dist = feature.distance != null ? `${feature.distance} мм` : '';
        return `Выдавить${dist ? ` ${dist}` : ''}`;
    }
    if (feature.kind === 'revolve') {
        const ang = feature.angle != null ? `${feature.angle}°` : '';
        return `Вращение${ang ? ` ${ang}` : ''}`;
    }
    if (feature.kind === 'primitive') {
        const labels = {
            box: 'Параллелепипед', cylinder: 'Цилиндр',
            sphere: 'Сфера', torus: 'Тор',
        };
        return labels[feature.primitiveKind] || 'Примитив';
    }
    if (feature.kind === 'combine') {
        const opLabels = { add: 'Объединение', cut: 'Вычитание', intersect: 'Пересечение' };
        return opLabels[feature.operation] || 'Логическая операция';
    }
    if (feature.kind === 'fillet') {
        const r = feature.radius != null ? `R${feature.radius}` : '';
        return `Скругление${r ? ` ${r}` : ''}`;
    }
    if (feature.kind === 'chamfer') {
        const r = feature.radius != null ? `${feature.radius} мм` : '';
        return `Фаска${r ? ` ${r}` : ''}`;
    }
    if (feature.kind === 'hole') {
        const d = feature.diameter != null ? `⌀${feature.diameter}` : '';
        return `Отверстие${d ? ` ${d}` : ''}`;
    }
    if (feature.kind === 'mirror') {
        const pl = feature.plane ? ` (${feature.plane})` : '';
        return `Зеркало${pl}`;
    }
    if (feature.kind === 'rectPattern') {
        const nx = feature.countX || 1, ny = feature.countY || 1;
        return `Массив ${nx}×${ny}`;
    }
    if (feature.kind === 'circPattern') {
        const n = feature.count || 1;
        return `Круговой массив ×${n}`;
    }
    return feature.kind;
};


const Timeline = () => {
    const features = useCadStore((s) => s.doc.features);
    const bodyMeshes = useCadStore((s) => s.bodyMeshes);
    const rollbackIndex = useCadStore((s) => s.rollbackIndex);
    const setRollbackIndex = useCadStore((s) => s.setRollbackIndex);

    // Перемотать историю до шага idx (эксклюзивно) или сбросить (null = всё).
    const rollTo = async (idx) => {
        setRollbackIndex(idx);
        try { await rebuildAllBodies(); }
        catch (err) { console.error('[Timeline] rollback rebuild failed:', err); }
    };

    // Если features пусто, но есть тела (test cube) — синтезируем
    const items = features.length > 0
        ? features
        : [...bodyMeshes.keys()].map((id, idx) => ({
            id, kind: 'box',
            __synthetic: true,
            label: `${RU.create.box} ${idx + 1}`,
        }));

    return (
        <div className={cl.timeline}>
            <div className={cl.header}>
                <span className={cl.headerIcon}><IconClock/></span>
                <span className={cl.headerLabel}>{RU.common.timeline}</span>
                <span className={cl.headerCount}>{items.length}</span>
                {rollbackIndex != null && (
                    <button
                        className={cl.headerCount}
                        style={{ marginLeft: 8, cursor: 'pointer', background: '#c9822a', color: '#fff' }}
                        title="Сбросить перемотку — построить всю модель"
                        onClick={() => rollTo(null)}
                    >▶ до конца</button>
                )}
            </div>
            <div className={cl.track}>
                {items.length === 0 ? (
                    <div className={cl.empty}>{RU.common.emptyTimeline}</div>
                ) : (
                    items.map((f, i) => (
                        <React.Fragment key={f.id}>
                            <FeatureCard
                                feature={f}
                                index={i + 1}
                                synthetic={f.__synthetic}
                                rolledBack={rollbackIndex != null && i >= rollbackIndex}
                            />
                            {/* Зона перемотки между этой и следующей карточкой */}
                            {!f.__synthetic && (
                                <button
                                    className={cl.rollbackGap}
                                    title={`Перемотать: построить первые ${i + 1} операц.`}
                                    onClick={() => rollTo(i + 1 >= items.length ? null : i + 1)}
                                    style={{
                                        alignSelf: 'stretch', width: 6, border: 'none',
                                        cursor: 'pointer', padding: 0,
                                        background: (rollbackIndex === i + 1) ? '#c9822a' : 'transparent',
                                    }}
                                />
                            )}
                        </React.Fragment>
                    ))
                )}
                <div className={cl.cursor}/>
            </div>
        </div>
    );
};


// sketchId, который использует фича (профиль/рёбра ссылаются на эскиз).
function usedSketchId(f) {
    const ref = (f.profileRefs && f.profileRefs[0]) || f.profileRef
        || (f.edgeRefs && f.edgeRefs[0]) || null;
    if (ref && typeof ref === 'string' && ref.includes('/')) return ref.split('/')[0];
    return null;
}

// Можно ли переставить фичу features[i] на позицию i+dir без нарушения
// зависимостей (эскиз должен идти раньше того, кто его использует; фичи с
// targetBodyIdx — после создающих тело). Возвращает {ok, reason}.
function canReorder(features, i, dir) {
    const j = i + dir;
    if (j < 0 || j >= features.length) return { ok: false, reason: 'край хронологии' };
    const a = features[i], b = features[j];
    // Нельзя утащить эскиз ПРАВЕЕ фичи, которая его использует.
    if (dir > 0 && a.kind === 'sketch') {
        if (usedSketchId(b) === (a.sketchId || a.id)) {
            return { ok: false, reason: 'этот эскиз используется следующей операцией' };
        }
    }
    // Нельзя утащить потребителя эскиза ЛЕВЕЕ самого эскиза.
    if (dir < 0 && b.kind === 'sketch') {
        if (usedSketchId(a) === (b.sketchId || b.id)) {
            return { ok: false, reason: 'операция зависит от эскиза слева' };
        }
    }
    // Фичи с targetBodyIdx (fillet/chamfer/shell/hole) не двигаем левее
    // первой создающей тело операции — упрощённо запрещаем перестановку
    // такой фичи через любую создающую тело (primitive/extrude/revolve/sweep/loft).
    const bodyMakers = new Set(['primitive', 'extrude', 'revolve', 'sweep', 'loft']);
    const dependsOnBody = (f) => typeof f.targetBodyIdx === 'number'
        || ['fillet', 'chamfer', 'shell', 'hole', 'combine'].includes(f.kind);
    if (dir < 0 && dependsOnBody(a) && bodyMakers.has(b.kind)) {
        return { ok: false, reason: 'операция зависит от тела, созданного слева' };
    }
    if (dir > 0 && dependsOnBody(b) && bodyMakers.has(a.kind)) {
        return { ok: false, reason: 'следующая операция зависит от этого тела' };
    }
    return { ok: true };
}


const FeatureCard = ({ feature, index, synthetic, rolledBack }) => {
    const activeFeatureId = useCadStore((s) => s.activeFeatureId);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const removeFeature = useCadStore((s) => s.removeFeature);
    const enterSketchMode = useCadStore((s) => s.enterSketchMode);
    const setFeatureSuppressed = useCadStore((s) => s.setFeatureSuppressed);
    const moveFeatureBy = useCadStore((s) => s.moveFeatureBy);

    const [menuOpen, setMenuOpen] = useState(false);

    const Icon = iconForFeature(feature);
    const label = featureLabel(feature);
    const isActive = feature.id === activeFeatureId;

    const onEdit = (e) => {
        e?.stopPropagation();
        setMenuOpen(false);
        if (synthetic) return; // у синтетических фич нет параметров
        if (feature.kind === 'extrude') {
            // Загружаем параметры в pendingFeature → откроется ExtrudeForm
            // в Properties с уже заполненными значениями
            setPendingFeature({
                kind: 'extrude',
                editingFeatureId: feature.id,
                profileRefs: feature.profileRefs || [],
                distance: feature.distance,
                direction: feature.direction,
                operation: feature.operation,
            });
        }
        if (feature.kind === 'revolve') {
            setPendingFeature({
                kind: 'revolve',
                editingFeatureId: feature.id,
                profileRefs: feature.profileRefs || [],
                axis: feature.axis,
                angle: feature.angle,
                operation: feature.operation,
            });
        }
        if (feature.kind === 'primitive') {
            // Edit-mode для примитива: загружаем все параметры + position.
            // PrimitiveForm не делает live-create в edit-mode (тело уже в сцене),
            // но pos и параметры всё равно дают live-update через debounce.
            setPendingFeature({
                kind: 'primitive',
                editingFeatureId: feature.id,
                primitiveKind: feature.primitiveKind,
                width:  feature.width,
                depth:  feature.depth,
                height: feature.height,
                radius: feature.radius,
                tube:   feature.tube,
                position: feature.position || [0, 0, 0],
                operation: feature.operation || 'new',
            });
        }
        if (feature.kind === 'hole') {
            // Edit-mode для отверстия безопасен: параметры (диаметр,
            // глубина, смещение) числовые и стабильные, не зависят от
            // runtime-id рёбер. faceInfo восстанавливаем из полей фичи.
            setPendingFeature({
                kind: 'hole',
                editingFeatureId: feature.id,
                faceInfo: {
                    bodyId: feature.bodyId,
                    faceId: feature.faceId,
                    planeRef: feature.planeRef,
                    faceCenter: feature.faceCenter,
                },
                diameter: feature.diameter,
                depth: feature.depth,
                depthType: feature.depthType || 'blind',
                offsetX: feature.offsetX || 0,
                offsetY: feature.offsetY || 0,
            });
        }
        if (feature.kind === 'mirror') {
            // Edit-mode для зеркала: плоскость симметрии — простой
            // параметр, тело резолвится по sourceBodyIdx при rebuild.
            setPendingFeature({
                kind: 'mirror',
                editingFeatureId: feature.id,
                bodyId: feature.bodyId,
                plane: feature.plane || 'YZ',
            });
        }
        if (feature.kind === 'rectPattern') {
            // Edit-mode для массива: все параметры числовые и стабильные.
            setPendingFeature({
                kind: 'rectPattern',
                editingFeatureId: feature.id,
                bodyId: feature.bodyId,
                countX: feature.countX || 1,
                stepX: feature.stepX || 0,
                countY: feature.countY || 1,
                stepY: feature.stepY || 0,
            });
        }
        if (feature.kind === 'circPattern') {
            setPendingFeature({
                kind: 'circPattern',
                editingFeatureId: feature.id,
                bodyId: feature.bodyId,
                axis: feature.axis || 'Z',
                count: feature.count || 2,
                angle: feature.angle != null ? feature.angle : 360,
            });
        }
        if (feature.kind === 'fillet' || feature.kind === 'chamfer') {
            // Edit-mode теперь работает: рёбра сохранены как устойчивые
            // дескрипторы (edgeDescs = midpoint+length), переживающие rebuild.
            // Загружаем их в pendingFeature — FilletChamferForm покажет радиус
            // и позволит поменять (или до-выбрать рёбра).
            setPendingFeature({
                kind: feature.kind,
                editingFeatureId: feature.id,
                edgeRefs: feature.edgeRefs || [],
                edgeDescs: feature.edgeDescs || [],
                edgeGeoms: feature.edgeGeoms || [],
                radius: feature.radius,
                targetBodyIdx: feature.targetBodyIdx,
            });
        }
        // combine edit не делаем: после булевой операции исходные тела
        // исчезают, параметрически менять нечего кроме типа операции —
        // проще удалить (🗑) и создать заново. (Не открываем пустую форму.)
        if (feature.kind === 'sketch') {
            // Двойной клик по эскизу в хронологии — открыть его на
            // редактирование (как двойной клик по «Эскиз N» в дереве).
            enterSketchMode(feature.sketchId || feature.id);
        }
    };

    const onRemove = async (e) => {
        e?.stopPropagation();
        setMenuOpen(false);
        if (synthetic) return;
        if (!window.confirm(`Удалить операцию «${label}»?`)) return;
        removeFeature(feature.id);
        // После удаления — полный rebuild оставшихся фич с нуля.
        try {
            await rebuildAllBodies();
        } catch (err) {
            console.error('[Timeline] rebuild after remove failed:', err);
        }
    };

    const onToggleSuppress = async (e) => {
        e?.stopPropagation();
        setMenuOpen(false);
        if (synthetic) return;
        setFeatureSuppressed(feature.id, !feature.suppressed);
        // Пересобираем — подавленная фича выпадет / включённая вернётся.
        try {
            await rebuildAllBodies();
        } catch (err) {
            console.error('[Timeline] rebuild after suppress failed:', err);
        }
    };

    const onMove = async (dir, e) => {
        e?.stopPropagation();
        setMenuOpen(false);
        if (synthetic) return;
        const feats = useCadStore.getState().doc.features;
        const i = feats.findIndex((f) => f.id === feature.id);
        const { ok, reason } = canReorder(feats, i, dir);
        if (!ok) {
            useCadStore.getState().pushToast('Нельзя переставить: ' + reason, 'error');
            return;
        }
        moveFeatureBy(feature.id, dir);
        try { await rebuildAllBodies(); }
        catch (err) { console.error('[Timeline] rebuild after reorder failed:', err); }
    };

    const suppressed = !!feature.suppressed;
    const dimmed = suppressed || rolledBack;
    /*
     * Операция не построилась — помечаем красной рамкой и значком, как в
     * Fusion. Иначе она выглядит выполненной: строка в хронологии есть, а
     * геометрии нет, и человек не понимает, куда делось скругление.
     * Причина видна в подсказке при наведении.
     */
    const failed = !!feature.__error;
    return (
        <div
            className={`${cl.card} ${isActive ? cl.cardActive : ''}`}
            title={failed
                ? `${index}. ${label} — ОШИБКА: ${feature.__error}`
                : `${index}. ${label}${suppressed ? ' (подавлено)' : ''}${rolledBack ? ' (перемотано)' : ''}`}
            style={{
                ...(dimmed ? { opacity: 0.4 } : null),
                ...(failed ? {
                    borderColor: '#d05050',
                    boxShadow: 'inset 0 0 0 1px #d05050',
                } : null),
            }}
            onDoubleClick={onEdit}
            onContextMenu={(e) => {
                e.preventDefault();
                setMenuOpen(true);
            }}
            onMouseLeave={() => setMenuOpen(false)}
        >
            <div className={cl.cardIcon}><Icon/></div>
            <div className={cl.cardLabel}>{label}</div>
            <div className={cl.cardIndex}>{index}</div>
            {failed && (
                <div
                    style={{
                        position: 'absolute', top: 1, right: 1,
                        width: 13, height: 13, borderRadius: '50%',
                        background: '#d05050', color: '#fff',
                        font: '700 10px/13px system-ui, sans-serif',
                        textAlign: 'center', pointerEvents: 'none',
                    }}
                >!</div>
            )}
            {/* Микро-кнопки на hover */}
            {!synthetic && (
                <div className={cl.cardActions}>
                    <button className={cl.cardActionBtn} onClick={onEdit} title="Редактировать"><IconEdit/></button>
                    <button className={cl.cardActionBtn} onClick={onRemove} title="Удалить"><IconTrash/></button>
                </div>
            )}
            {menuOpen && !synthetic && (
                <div className={cl.contextMenu}>
                    <button onClick={onEdit}>Редактировать</button>
                    <button onClick={(e) => onMove(-1, e)}>← Сдвинуть раньше</button>
                    <button onClick={(e) => onMove(1, e)}>Сдвинуть позже →</button>
                    <button onClick={onToggleSuppress}>
                        {suppressed ? 'Включить' : 'Подавить'}
                    </button>
                    <button onClick={onRemove}>Удалить</button>
                </div>
            )}
        </div>
    );
};

export default Timeline;
